import { translateCustomerDocumentType } from '@common/intl/customer-document-type'
import type { CustomerEventFragment } from '@gql'
import { CustomerEventType } from '@gql'
import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'
import type { MessageValue } from 'react-intl'

export function translateCustomerEvent(
  intl: IntlShape,
  event: CustomerEventFragment,
): string | ReactNode {
  switch (event.type) {
    case CustomerEventType.CREATED:
      return intl.formatMessage({
        defaultMessage: 'Customer was created',
        description: 'customer history message',
      })
    case CustomerEventType.UPDATED:
      return intl.formatMessage({
        defaultMessage: 'Customer was updated',
        description: 'customer history message',
      })
    case CustomerEventType.ADDRESS_ADDED:
      return intl.formatMessage({
        defaultMessage: 'Address was added',
        description: 'customer history message',
      })
    case CustomerEventType.ADDRESS_DEFAULT:
      return intl.formatMessage({
        defaultMessage: 'Address was set as default',
        description: 'customer history message',
      })
    case CustomerEventType.ADDRESS_REMOVED:
      return intl.formatMessage({
        defaultMessage: 'Address was removed',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_EMAIL_ADDED:
      return intl.formatMessage({
        defaultMessage: 'Email was added',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_EMAIL_DEFAULT:
      return intl.formatMessage({
        defaultMessage: 'Email was set as default',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_EMAIL_REMOVED:
      return intl.formatMessage({
        defaultMessage: 'Email was removed',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_PHONE_ADDED:
      return intl.formatMessage({
        defaultMessage: 'Phone was added',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_PHONE_DEFAULT:
      return intl.formatMessage({
        defaultMessage: 'Phone was set as default',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_PHONE_REMOVED:
      return intl.formatMessage({
        defaultMessage: 'Phone was removed',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_EMAIL_VERIFIED:
      return intl.formatMessage({
        defaultMessage: 'Email was verified',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_PHONE_VERIFIED:
      return intl.formatMessage({
        defaultMessage: 'Phone was verified',
        description: 'customer history message',
      })
    case CustomerEventType.CONTACT_VERIFICATION_CONFLICT:
      // A portal claim proved control of a contact already verified for
      // another customer; verification was rejected. Surfaces the duplicate
      // so an operator can merge the two customers.
      return event.parameters.value
        ? intl.formatMessage<{ readonly value: MessageValue }>(
            {
              defaultMessage:
                'Verification conflict: {value} is already verified for another customer',
              description: 'customer history message',
            },
            { value: <b>{event.parameters.value}</b> },
          )
        : intl.formatMessage({
            defaultMessage:
              'Verification conflict: contact is already verified for another customer',
            description: 'customer history message',
          })
    case CustomerEventType.CUSTOMER_MERGED:
      // This customer absorbed another (the source) via an operator merge.
      return event.parameters.sourceProfile?.name
        ? intl.formatMessage<{ readonly name: MessageValue }>(
            {
              defaultMessage: 'Customer {name} was merged into this one',
              description: 'customer history message',
            },
            { name: <b>{event.parameters.sourceProfile.name}</b> },
          )
        : intl.formatMessage({
            defaultMessage: 'Another customer was merged into this one',
            description: 'customer history message',
          })
    case CustomerEventType.DOCUMENT_UPLOADED:
      if (event.parameters.type) {
        return intl.formatMessage<{ readonly type: MessageValue }>(
          {
            defaultMessage: 'Document {type} was uploaded',
            description: 'customer history message',
          },
          {
            type: (
              <b>
                {translateCustomerDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Document was uploaded',
          description: 'customer history message',
        })
      }

    case CustomerEventType.DOCUMENT_EDITED:
      if (event.parameters.type) {
        return intl.formatMessage<{ readonly type: MessageValue }>(
          {
            defaultMessage: 'Document {type} was edited',
            description: 'customer history message',
          },
          {
            type: (
              <b>
                {translateCustomerDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Document was edited',
          description: 'customer history message',
        })
      }

    case CustomerEventType.DOCUMENT_DELETED:
      if (event.parameters.type) {
        return intl.formatMessage<{ readonly type: MessageValue }>(
          {
            defaultMessage: 'Document {type} was deleted',
            description: 'customer history message',
          },
          {
            type: (
              <b>
                {translateCustomerDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Document was deleted',
          description: 'customer history message',
        })
      }

    case CustomerEventType.CUSTOMER_ANONYMIZED:
      return intl.formatMessage({
        defaultMessage: 'Customer data was anonymized',
        description: 'customer history message',
      })

    default:
      return intl.formatMessage({
        defaultMessage: 'Customer event',
        description: 'customer history message',
      })
  }
}
